// ENG-10028: the single source of truth for how a Slack channel config becomes
// slack-channel MCP boot-time env.
//
// WHY THIS FILE EXISTS. Three separate places have to agree on the same
// normalisation, and until now only two of them existed and they agreed only by
// hand-copied comment:
//
//   1. the claudecode adapter's PERSISTENT Slack branch, which writes .mcp.json;
//   2. the same adapter's ONESHOT branch, a near-duplicate of (1);
//   3. the manager's `extractSlackBehaviourSubset` (apps/cli), which hashes the
//      operator-editable subset to decide whether the running MCP child needs a
//      restart to see the change.
//
// (1) and (2) have already drifted once — CodeRabbit on PR #934 found the
// persistent branch missing the peer wiring the oneshot branch had, which left
// `SLACK_PEERS` empty for most agents. (3) drifting from (1) is worse and quieter:
// the env changes on disk and the restart never fires, so the running process
// keeps enforcing the old value with no error anywhere. That is the "last-hop
// gap" this codebase has now closed four times (ENG-5858 sender_policy,
// ENG-6024 Teams, ENG-6035/ENG-6464 Slack behaviour, and this ticket).
//
// Sharing the functions rather than the intent is the point. A comment saying
// "mirror the adapter's filter exactly" is only as good as the next editor
// reading it; a shared function cannot be half-updated.
//
// EVERY FUNCTION HERE IS PURE AND TOTAL. The channel config is operator-editable
// JSONB, so each one accepts `unknown` and coerces rather than trusting a type.

/**
 * `peer_agent_mode` → the value of `SLACK_PEER_AGENT_MODE`, or `''` when the
 * adapter emits nothing.
 *
 * Only `listen` and `respond` are emitted. `off`, absent, and anything
 * malformed all produce NO env var, which the classifier reads as the
 * deny-by-default state — so they must normalise to the same value here, or an
 * operator toggling `respond` → `off` would look like a change that needs a
 * restart when the env is byte-identical (and, worse, `off` → absent would look
 * like no change at all if we normalised the other way round).
 */
export function normaliseSlackPeerAgentMode(raw: unknown): string {
  return raw === 'listen' || raw === 'respond' ? raw : '';
}

/**
 * `peer_group_ids` → the ids that reach `SLACK_PEER_GROUP_IDS`, in order.
 *
 * Order is preserved because the adapter emits a `join(',')` and the classifier
 * splits it, so a reorder IS a different env value. Numbers are coerced because
 * the adapter does (a JSONB array can carry them); anything else becomes '' and
 * is dropped, so a malformed entry the adapter would discard cannot register as
 * a change.
 */
export function normaliseSlackPeerGroupIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : ''))
    .filter((v) => v.length > 0);
}

/**
 * `third_party_bots` → the exact string written to `SLACK_THIRD_PARTY_BOTS`, or
 * `undefined` when the adapter emits nothing.
 *
 * SHARED BY BOTH ADAPTER BRANCHES ON PURPOSE. The persistent and oneshot Slack
 * env blocks are near-duplicates and have already drifted once (CodeRabbit, PR
 * #934). A helper called from both cannot drift that way again — and ENG-10028
 * adds the manager as a third caller for the same reason.
 *
 * VALIDATES RATHER THAN STRINGIFYING BLINDLY, mirroring `parseThirdPartyBotsEnv`
 * exactly. The reader drops a malformed entry silently and returns the closed
 * state on a bad parse; if this side wrote something the reader then discarded,
 * the operator would see a configured allowlist that does nothing and no error
 * anywhere. Rejecting the same shapes here means what we write is what the
 * reader accepts.
 *
 * Returns `undefined` (emit nothing) rather than `'[]'` when there is nothing
 * valid to write, so an agent with no grants keeps a byte-identical env block
 * and does not churn the credentials hash.
 */
export function buildSlackThirdPartyBotsEnvValue(raw: unknown): string | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;

  const entries: Array<Record<string, unknown>> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const botUserId = typeof rec['bot_user_id'] === 'string' ? rec['bot_user_id'].trim() : '';
    if (!botUserId) continue;
    // An ABSENT channel_ids is malformed, not a deliberate grant to nowhere —
    // same rule as the reader. An EXPLICIT [] stays valid.
    if (!Array.isArray(rec['channel_ids'])) continue;
    const channelIds = rec['channel_ids']
      .filter((c): c is string => typeof c === 'string' && c.trim() !== '')
      .map((c) => c.trim());
    const botId =
      typeof rec['bot_id'] === 'string' && rec['bot_id'].trim() ? rec['bot_id'].trim() : undefined;
    const label =
      typeof rec['label'] === 'string' && rec['label'].trim() ? rec['label'].trim() : undefined;
    entries.push({
      bot_user_id: botUserId,
      channel_ids: channelIds,
      ...(botId ? { bot_id: botId } : {}),
      ...(label ? { label } : {}),
    });
  }

  return entries.length > 0 ? JSON.stringify(entries) : undefined;
}
